import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Stack } from '@mui/material';
import {
  Loader,
  NothingFoundAlert,
  PageContentLayout,
  PageHeaderLayout,
  SideMenu,
  Typography,
} from 'melp-design/components';
import { ROUTES } from 'config';
import { useRouteMatch } from 'react-router-dom';
import { useRouteParams } from 'utils/useIdParam';
import { useAdminCard } from 'store/admin-cards';

export const AdminCardLayout: FC = ({ children }) => {
  const { t } = useTranslation();
  const { path } = useRouteMatch();
  const { id } = useRouteParams(ROUTES.admin.cards.settings);

  const { data: card, isLoading } = useAdminCard(id);

  if (isLoading) return <Loader />;
  if (!card) return <NothingFoundAlert />;

  return (
    <Stack gap={2}>
      <PageHeaderLayout
        breadcrumbs={[
          {
            label: t('cards.title'),
            url: ROUTES.admin.cards.list,
          },
          {
            label: card?.brand?.name ?? '',
          },
        ]}
        under={
          card ? (
            <Typography color="textSecondary">
              {t('marketplace.employeeID', { id: card.sequence })}
            </Typography>
          ) : null
        }
      />

      <PageContentLayout
        sidebar={
          <SideMenu
            tabs={[
              {
                key: 'settings',
                label: t('common.settings'),
                url: ROUTES.admin.cards.settings.replace(':id', id),
                isActive: path === ROUTES.admin.cards.settings,
                disabled: !card,
              },
              {
                key: 'price',
                label: t('marketplaceItems.marketplaceItemPriceSettings'),
                url: ROUTES.admin.cards.price.replace(':id', id),
                isActive: path === ROUTES.admin.cards.price,
                disabled: !card,
              },
              {
                key: 'content',
                label: t('benefitTemplates.templateContent'),
                url: ROUTES.admin.cards.content.replace(':id', id),
                isActive: path === ROUTES.admin.cards.content,
                disabled: !card,
              },
              {
                key: 'fees',
                label: t('cards.fees.title'),
                url: ROUTES.admin.cards.fees.replace(':id', id),
                isActive: path === ROUTES.admin.cards.fees,
                disabled: !card,
              },
              ...(card?.priceType === 'fixed'
                ? [
                    {
                      key: 'vouchers',
                      label: t('shop.vouchers.title'),
                      url: ROUTES.admin.cards.vouchers.replace(':id', id),
                      isActive: path === ROUTES.admin.cards.vouchers,
                    } as const,
                  ]
                : []),
            ]}
          />
        }
      >
        {children}
      </PageContentLayout>
    </Stack>
  );
};
